/**
 * Layout of the unauthenticated screens.
 *
 * A two-panel composition: the brand panel carries the product identity and is hidden below `lg` where vertical
 * space is scarce, while the form column stays narrow enough to keep line length readable. The locale and theme
 * controls stay reachable, because a visitor must be able to switch language before they have an account.
 */

import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LocaleSwitcher } from '@/components/layout/LocaleSwitcher';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { Card } from '@/components/ui/surface';

/** Chrome shared by the sign-in, registration and recovery screens. */
export function AuthLayout() {
  const { t } = useTranslation();

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">
      <aside className="relative hidden flex-col justify-between bg-sidebar p-10 text-sidebar-foreground lg:flex">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="grid h-9 w-9 place-items-center rounded-md bg-primary text-primary-foreground"
          >
            PF
          </span>
          <span className="text-lg font-semibold">{t('common.appName')}</span>
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-3xl font-bold leading-snug">{t('auth.headline')}</h2>
          <p className="text-sm text-sidebar-foreground/80">{t('auth.headlineBody')}</p>
        </div>

        <p className="text-xs text-sidebar-foreground/60">{t('landing.footer')}</p>
      </aside>

      <div className="flex flex-col">
        <div className="flex items-center justify-between p-4 lg:justify-end">
          <span className="flex items-center gap-2 lg:hidden">
            <span
              aria-hidden
              className="grid h-8 w-8 place-items-center rounded-md bg-primary text-sm text-primary-foreground"
            >
              PF
            </span>
            <span className="font-semibold">{t('common.appName')}</span>
          </span>

          <div className="flex items-center gap-2">
            <LocaleSwitcher />
            <ThemeToggle />
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center px-4 pb-10">
          <Card className="w-full max-w-md p-6 sm:p-8">
            <Outlet />
          </Card>
        </div>
      </div>
    </div>
  );
}
